import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Users, Edit, Trash2, AlertTriangle, CreditCard, Phone, Mail, Star } from 'lucide-react';
import { OverallStars } from './ScorecardFormDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const IncidentSummary = ({ stats, driverId }) => {
  if (!stats) {
    return (
      <div className="flex items-center gap-2 mb-4 text-xs text-slate-400 border-t pt-3">
        <AlertTriangle className="w-3.5 h-3.5" />
        <span>No incidents in the last 12 months</span>
      </div>
    );
  }
  const isHigh = stats.total > 2;
  return (
    <div className="flex items-center justify-between mb-4 border-t pt-3" data-testid={`driver-incidents-${driverId}`}>
      <div className="flex items-center gap-2">
        <AlertTriangle className={`w-4 h-4 ${isHigh ? 'text-red-500' : 'text-amber-500'}`} />
        <div className="leading-tight">
          <p className={`text-sm font-heading font-bold ${isHigh ? 'text-red-600' : 'text-amber-700'}`}>
            {stats.total} incident{stats.total !== 1 ? 's' : ''}
          </p>
          <p className="text-[10px] text-slate-500 font-mono uppercase tracking-wider">Last 12 months</p>
        </div>
      </div>
      <div className="text-right">
        <p className="text-xs font-data text-slate-700">
          R {(stats.damage_total || 0).toLocaleString('en-ZA', { maximumFractionDigits: 0 })}
        </p>
        <p className="text-[10px] text-slate-500 font-mono uppercase tracking-wider">damage</p>
      </div>
    </div>
  );
};

const DriverCard = ({ driver, incidentStats, onEdit, onDelete, onScore }) => {
  const photoSrc = driver.photo_url
    ? (driver.photo_url.startsWith('http') ? driver.photo_url : `${API_URL}${driver.photo_url}`)
    : null;

  return (
    <Card className={`card-hover border-slate-200 ${(driver.license_due || driver.pdp_due) ? 'border-amber-300' : ''}`}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center overflow-hidden">
              {photoSrc
                ? <img src={photoSrc} alt={driver.name} className="w-full h-full object-cover" />
                : <Users className="w-5 h-5 text-slate-600" />
              }
            </div>
            <div>
              <h3 className="font-heading font-semibold text-obsidian">{driver.name}</h3>
              {driver.employee_id && (
                <p className="text-xs text-slate-500 font-data">ID: {driver.employee_id}</p>
              )}
            </div>
          </div>
          <Badge className={`text-xs ${driver.status === 'active' ? 'status-active' : 'status-inactive'}`}>
            {driver.status}
          </Badge>
        </div>

        <div className="space-y-2 mb-4">
          <div className="flex items-center gap-2 text-sm">
            <CreditCard className="w-4 h-4 text-slate-400" />
            <span className="font-data text-slate-600">
              {driver.license_number || <span className="text-slate-400 italic">no licence on file</span>}
            </span>
            {driver.license_type && (
              <Badge variant="outline" className="text-xs">{driver.license_type}</Badge>
            )}
          </div>
          {driver.phone && (
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <Phone className="w-4 h-4" />
              <span>{driver.phone}</span>
            </div>
          )}
          {driver.email && (
            <div className="flex items-center gap-2 text-sm text-slate-500">
              <Mail className="w-4 h-4" />
              <span className="truncate">{driver.email}</span>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2 mb-4">
          {driver.license_due && (
            <Badge className="alert-badge alert-badge-error text-xs">
              License expires {driver.license_expiry}
            </Badge>
          )}
          {driver.pdp_due && (
            <Badge className="alert-badge alert-badge-warning text-xs">
              PDP expires {driver.pdp_expiry}
            </Badge>
          )}
        </div>

        <p className="text-sm text-slate-500 mb-2" data-testid={`driver-vehicle-${driver.id}`}>
          {driver.assigned_vehicle_name
            ? <>Assigned: <span className="font-medium text-obsidian">{driver.assigned_vehicle_name}</span></>
            : <span className="italic text-slate-400">No vehicle assigned</span>}
        </p>
        <div className="mb-4" data-testid={`driver-score-${driver.id}`}>
          {driver.score_count > 0 ? (
            <div className="flex items-center gap-2">
              <OverallStars value={driver.score_avg} />
              <span className="text-xs text-slate-400">avg of {driver.score_count} scorecard{driver.score_count === 1 ? '' : 's'}</span>
            </div>
          ) : (
            <span className="text-xs text-slate-400">Not scored yet</span>
          )}
        </div>

        <IncidentSummary stats={incidentStats} driverId={driver.id} />

        <div className="flex items-center gap-2 pt-3 border-t border-slate-100">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onEdit(driver)}
            data-testid={`edit-driver-${driver.id}`}
          >
            <Edit className="w-4 h-4 mr-1" />
            Edit
          </Button>
          {onScore && (
            <Button variant="ghost" size="sm" onClick={() => onScore(driver)} data-testid={`score-driver-${driver.id}`}>
              <Star className="w-4 h-4 mr-1" />
              Score
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            className="text-red-500 hover:text-red-600 hover:bg-red-50"
            onClick={() => onDelete(driver)}
            data-testid={`delete-driver-${driver.id}`}
          >
            <Trash2 className="w-4 h-4 mr-1" />
            Delete
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default DriverCard;
